import React from 'react';
import {
  Accordions,
  Button,
  Form,
  FormLabel,
  FormGroup,
  Gutter,
  Input,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Radio,
  TaxonomyUtil,
} from '@axiom/ui';
import type {
  Candidate,
  CandidateAlertWorkFeed,
  Taxonomy,
} from '@axiom/validation';
import { CandidateAlertWorkFeedSchema } from '@axiom/validation';
import { CandidateOppFeedAlertsConst } from '@axiom/const';

import { CandidateAlertsWorkFeedApi } from '../../api/candidate-alerts-work-feed-api';
import { WorkFeedFilterAccordion } from '../WorkFeedFilterAccordion/WorkFeedFilterAccordion';
import { WorkFeedFilterUtil } from '../../utils/work-feed-filter-util';
import type { Option, FilterPill } from '../../types/work-feed.types';

import { WorkFeedAlertsPills } from './WorkFeedAlertsPills';

const { Frequency } = CandidateOppFeedAlertsConst;

const {
  countryOptions,
  engagementLengthOptions,
  weeklyCommitmentOptions,
  worksiteOptions,
} = WorkFeedFilterUtil;

export type WorkFeedAlertsModalProps = {
  candidateId: Candidate['id'];
  onClose: () => void;
  workFeedAlert: CandidateAlertWorkFeed;
  taxonomy: Taxonomy;
  name?: string;
};

export const WorkFeedAlertsModal = ({
  candidateId,
  onClose,
  workFeedAlert,
  taxonomy,
  name = 'WORK_FEED_ALERTS_MODAL',
}: WorkFeedAlertsModalProps) => {
  const practiceAreaOptions = TaxonomyUtil.getPracticeAreaOptions(
    taxonomy.practiceAreas
  );
  const focusAreaOptions = TaxonomyUtil.getFocusAreaOptions(
    taxonomy.focusAreas
  );
  const industryOptions = TaxonomyUtil.getFilteredIndustryOptions(
    taxonomy.industries
  );
  const businessTitleOptions = TaxonomyUtil.getBusinessTitleOptions(
    taxonomy.businessTitles
  );

  const removeFilter = (
    filterName: string,
    value: string,
    values: CandidateAlertWorkFeed
  ) => {
    const filter = [
      ...(values[filterName as keyof typeof values] as unknown[]),
    ];
    const index = filter.indexOf(value);
    filter.splice(index, 1);
    return filter;
  };

  const isOptionsExpanded = (
    isExpanded: boolean,
    id: string,
    options: Option[],
    maxChildrenShown: number
  ) => {
    if (isExpanded) {
      return isExpanded;
    }
    const index = options.findIndex(option => option.value === id);
    return index >= maxChildrenShown;
  };

  const initialValues = {
    ...workFeedAlert,
    practiceAreas: TaxonomyUtil.filterSelectablePracticeAreas(
      taxonomy,
      workFeedAlert.practiceAreas ?? []
    ),
    focusAreas: TaxonomyUtil.filterSelectableFocusAreas(
      taxonomy,
      workFeedAlert.focusAreas ?? []
    ),
  };

  return (
    <Form
      schema={CandidateAlertWorkFeedSchema}
      initialValues={initialValues}
      onSubmit={async changedData => {
        const selectedPracticeAreas =
          changedData.practiceAreas ?? initialValues.practiceAreas;
        const selectedFocusAreas =
          changedData.focusAreas ?? initialValues.focusAreas;

        await CandidateAlertsWorkFeedApi.updateCandidateAlert(
          candidateId,
          workFeedAlert.id as string,
          {
            ...changedData,
            practiceAreas: selectedPracticeAreas,
            focusAreas: selectedFocusAreas,
          }
        );

        await CandidateAlertsWorkFeedApi.refreshCandidateAlerts(candidateId);
        onClose();
      }}
    >
      {({ fireSubmit, setValues, values }) => {
        return (
          <Modal name={name}>
            <ModalHeader name="WORK_FEED_ALERTS_MODAL_HEADER" onClose={onClose}>
              Edit Alert
            </ModalHeader>
            <ModalSection>
              <Input name="alertName" label="Alert name" />
              <Gutter bottom="24px" />
              <FormGroup label="Email Frequency">
                <Radio
                  name="frequency"
                  option={Frequency.Instant}
                  displayValue="Instant"
                />
                <Gutter bottom="8px" />
                <Radio
                  name="frequency"
                  option={Frequency.Daily}
                  displayValue="Daily"
                />
                <Gutter bottom="8px" />
                <Radio
                  name="frequency"
                  option={Frequency.Weekly}
                  displayValue="Weekly"
                />
              </FormGroup>
              <Gutter bottom="24px" />
              <FormLabel>Email alert preferences</FormLabel>
              <Gutter bottom="8px" />
              <WorkFeedAlertsPills
                workFeedAlert={values as CandidateAlertWorkFeed}
                onClear={async (pill: FilterPill) => {
                  const { name: pillName, value } = pill;
                  const filter = removeFilter(
                    pillName,
                    value,
                    values as CandidateAlertWorkFeed
                  );
                  await setValues({
                    ...values,
                    [pillName]: filter,
                  });
                }}
                onClearAll={async () => {
                  await setValues({
                    ...values,
                    practiceAreas: [],
                    focusAreas: [],
                    industries: [],
                    weeklyCommitments: [],
                    worksites: [],
                    engagementLengths: [],
                    countries: [],
                    businessTitles: [],
                  });
                }}
              />
              <Accordions name="WORK_FEED_ALERTS_ACCORDIONS">
                <WorkFeedFilterAccordion
                  count={values.practiceAreas?.length ?? 0}
                  expandAccordion={!!workFeedAlert.practiceAreas?.length}
                  expandOptions={workFeedAlert.practiceAreas?.reduce(
                    (isExpanded, id) =>
                      isOptionsExpanded(isExpanded, id, practiceAreaOptions, 6),
                    false
                  )}
                  maxChildrenShown={6}
                  name="practiceAreas"
                  options={practiceAreaOptions}
                  title="Practice Area"
                />
                <WorkFeedFilterAccordion
                  count={values.focusAreas?.length ?? 0}
                  expandAccordion={!!workFeedAlert.focusAreas?.length}
                  expandOptions={workFeedAlert.focusAreas?.reduce(
                    (isExpanded, id) =>
                      isOptionsExpanded(isExpanded, id, focusAreaOptions, 6),
                    false
                  )}
                  maxChildrenShown={6}
                  name="focusAreas"
                  options={focusAreaOptions}
                  title="Focus Area"
                />
                <WorkFeedFilterAccordion
                  count={values.industries?.length ?? 0}
                  expandAccordion={!!workFeedAlert.industries?.length}
                  expandOptions={workFeedAlert.industries?.reduce(
                    (isExpanded, id) =>
                      isOptionsExpanded(isExpanded, id, industryOptions, 6),
                    false
                  )}
                  maxChildrenShown={6}
                  name="industries"
                  options={industryOptions}
                  title="Industry"
                />
                <WorkFeedFilterAccordion
                  count={values.weeklyCommitments?.length ?? 0}
                  expandAccordion={!!workFeedAlert.weeklyCommitments?.length}
                  name="weeklyCommitments"
                  options={weeklyCommitmentOptions}
                  title="Weekly Commitment"
                />
                <WorkFeedFilterAccordion
                  count={values.countries?.length ?? 0}
                  expandAccordion={!!workFeedAlert.countries?.length}
                  name="countries"
                  options={countryOptions}
                  title="Country"
                />
                <WorkFeedFilterAccordion
                  count={values.worksites?.length ?? 0}
                  expandAccordion={!!workFeedAlert.worksites?.length}
                  name="worksites"
                  options={worksiteOptions}
                  title="Worksite"
                />
                <WorkFeedFilterAccordion
                  count={values.engagementLengths?.length ?? 0}
                  expandAccordion={!!workFeedAlert.engagementLengths?.length}
                  name="engagementLengths"
                  options={engagementLengthOptions}
                  title="Engagement Length"
                />
                <WorkFeedFilterAccordion
                  count={values.businessTitles?.length ?? 0}
                  expandAccordion={!!workFeedAlert.businessTitles?.length}
                  name="businessTitles"
                  options={businessTitleOptions}
                  title="Legal Role"
                />
              </Accordions>
            </ModalSection>
            <ModalFooter>
              <Button variation="outline" name="CANCEL" onClick={onClose}>
                Cancel
              </Button>
              <Button name="SUBMIT" onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
